import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MultiSelectModule } from 'primeng/multiselect';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import {
  DeliveryDateSort,
  DeliveryDateSortField,
  DeliveryDateTableComponent,
} from '../../shared/components/delivery-date-table/delivery-date-table.component';
import { PaginationComponent } from '../../shared/components/pagination/pagination.component';
import { WERK_OPTIONS } from '../../shared/constants/order-document-labels';
import { EStorageKeys } from '../../shared/enums/storage-keys.enum';
import { ILieferterminOverviewRow, WerkType } from '../../shared/models/order-document.model';
import { FilterStorageService } from '../../shared/services/filter-storage.service';
import { OrderDocumentService } from '../../shared/services/order-document.service';
import { SocketService } from '../../shared/services/socket.service';

/**
 * In-app Liefertermine page: every delivery date from today onwards, searchable, sortable and paged
 * on the server. Shares its table with the production TV board (`/delivery-dates-overview`) via
 * `app-delivery-date-table`, rendered here in the compact size with the standard page chrome.
 */
@Component({
  selector: 'app-delivery-dates',
  standalone: true,
  imports: [ReactiveFormsModule, MultiSelectModule, DeliveryDateTableComponent, PaginationComponent],
  templateUrl: './delivery-dates.component.html',
  styleUrls: ['./delivery-dates.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeliveryDatesComponent implements OnInit {
  private readonly orderDocumentService = inject(OrderDocumentService);
  private readonly socketService = inject(SocketService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly filterStorageService = inject(FilterStorageService);

  readonly pageSize = 10;

  readonly searchControl = new FormControl('', { nonNullable: true });
  readonly werkFilterControl = new FormControl<WerkType[]>([], { nonNullable: true });
  readonly werkOptions = WERK_OPTIONS;

  readonly rows = signal<ILieferterminOverviewRow[]>([]);
  readonly loading = signal(true);
  readonly error = signal(false);
  readonly currentPage = signal(1);
  readonly totalItems = signal(0);
  readonly totalPages = signal(1);
  /** Default like the TV board: binding ("sicher") Liefertermine first, then by date. */
  readonly sort = signal<DeliveryDateSort>({ field: 'isBinding', direction: 'desc' });

  ngOnInit(): void {
    this.restoreWerkFilter();
    this.load();

    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.currentPage.set(1);
        this.load();
      });

    this.werkFilterControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.currentPage.set(1);
      this.persistWerkFilter();
      this.load();
    });

    // Idempotent connect; the root singleton keeps one socket alive across the app.
    this.socketService.connect();

    // Refresh in place (current page/search/sort) whenever any order document's Liefertermine or
    // status changes elsewhere, so e.g. a newly completed order disappears without user action.
    this.socketService.liefertermineChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.load());
  }

  onPageChange(page: number): void {
    this.currentPage.set(page);
    this.load();
  }

  /** Toggles the direction when the same column is clicked again, otherwise sorts the new one ascending. */
  onSortChange(field: DeliveryDateSortField): void {
    const current = this.sort();
    this.sort.set({
      field,
      direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc',
    });
    this.currentPage.set(1);
    this.load();
  }

  clearSearch(): void {
    this.searchControl.setValue('');
  }

  openOrderDocument(row: ILieferterminOverviewRow): void {
    this.router.navigate(['/order-documents/edit', row.orderDocumentId]);
  }

  private load(): void {
    this.loading.set(true);
    const { field, direction } = this.sort();

    this.orderDocumentService
      .getUpcomingLiefertermine(
        this.currentPage(),
        this.pageSize,
        this.searchControl.value.trim(),
        field,
        direction,
        this.werkFilterControl.value.join(',')
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          const result = response.DATA;
          this.rows.set(result?.data ?? []);
          this.totalItems.set(result?.total ?? 0);
          this.totalPages.set(Math.max(1, result?.totalPages ?? 1));
          this.error.set(false);
          this.loading.set(false);
        },
        error: () => {
          this.rows.set([]);
          this.totalItems.set(0);
          this.error.set(true);
          this.loading.set(false);
        },
      });
  }

  private restoreWerkFilter(): void {
    const werke = this.filterStorageService.restore(
      EStorageKeys.DELIVERY_DATE_WERK_FILTER,
      this.werkOptions.map(o => o.value)
    );
    this.werkFilterControl.setValue(werke, { emitEvent: false });
  }

  private persistWerkFilter(): void {
    this.filterStorageService.persist(EStorageKeys.DELIVERY_DATE_WERK_FILTER, this.werkFilterControl.value);
  }
}
